import React from "react";
import { resolvePublicPageVariant } from "../../utils/publicPages";
import styles from "./PublicPageShell.module.css";

function resolveBackgroundStyle(variant) {
  if (variant.backgroundType === "image" && variant.backgroundImageUrl) {
    return {
      backgroundImage: `url(${variant.backgroundImageUrl})`,
    };
  }

  if (variant.backgroundType === "color" && variant.overlayColor) {
    return {
      background: variant.overlayColor,
    };
  }

  return {
    background: variant.backgroundGradient || "#0f172a",
  };
}

function resolveOverlayOpacity(variant, prefersStrongOverlay) {
  const raw =
    typeof variant.overlayOpacity === "number"
      ? variant.overlayOpacity
      : Number.parseFloat(`${variant.overlayOpacity ?? ""}`);
  const hasValue = Number.isFinite(raw);
  const fallback = prefersStrongOverlay ? 0.68 : 0.54;
  const nextValue = hasValue ? raw : fallback;
  const minimum = prefersStrongOverlay ? 0.62 : 0.44;
  return Math.max(minimum, Math.min(0.9, nextValue));
}

function getPageContentLabel(pageKey) {
  if (pageKey === "join") return "Invitation details";
  if (pageKey === "content") return "Page details";
  return "How this works";
}

export default function PublicPageShell({
  publicPages,
  pageKey,
  logoUrl,
  siteName,
  eyebrow = "",
  fallbackTitle = "",
  fallbackSubtitle = "",
  page = null,
  roleSummary = null,
  children,
  footer = null,
  panelEyebrow = "",
  panelTitle = "",
  panelDescription = "",
  panelFooter = null,
}) {
  const variant = resolvePublicPageVariant(publicPages, pageKey);
  const backgroundStyle = resolveBackgroundStyle(variant);
  const showVideo =
    variant.backgroundType === "video" && Boolean(variant.backgroundVideoUrl);
  const showMediaBackground =
    showVideo ||
    (variant.backgroundType === "image" && Boolean(variant.backgroundImageUrl));
  const showLogo = variant.logoVisibility !== false && Boolean(logoUrl || siteName);
  const pageTitle = variant.headline || page?.title || fallbackTitle;
  const pageSubtitle = variant.subheadline || fallbackSubtitle;
  const overlayOpacity = resolveOverlayOpacity(variant, showMediaBackground);
  const pageContentLabel = getPageContentLabel(pageKey);
  const hasPanelBodyContent = React.Children.toArray(children).some(
    (child) => child !== null && child !== undefined && child !== false
  );
  const hasPanelContent = Boolean(
    panelEyebrow ||
      panelTitle ||
      panelDescription ||
      panelFooter ||
      hasPanelBodyContent
  );
  const isSignupShell = pageKey === "signup" || pageKey === "join";

  return (
    <div
      className={`${styles.shell} ${
        variant.layout === "centered" ? styles.shellCentered : styles.shellSplit
      } ${pageKey === "content" ? styles.shellContentPage : ""} ${
        isSignupShell ? styles.shellSignup : ""
      } ${
        pageKey === "login" ? styles.shellLogin : ""
      }`}
    >
      <header className={styles.topHeader}>
        <nav className={styles.navPill} aria-label="Primary">
          <a className={styles.navBrand} href="/" aria-label={siteName || "Uniflo"}>
            {logoUrl ? (
              <img src={logoUrl} alt={siteName || "Uniflo"} className={styles.navLogo} />
            ) : (
              <span className={styles.navWordmark}>{siteName || "Uniflo"}</span>
            )}
          </a>
          <div className={styles.navLinks}>
            <a className={styles.navLink} href="/">
              Home
            </a>
            <a className={styles.navLink} href="/team-cohesion-app">
              Cohesion
            </a>
            <a className={styles.navLink} href="/templates">
              Templates
            </a>
            <a className={styles.navLink} href="/case-studies">
              Proof
            </a>
            <a className={styles.navLink} href="/pricing">
              Pricing
            </a>
          </div>
          <a className={styles.navCta} href="/get-started/uniflo-essentials">
            Start from R69
          </a>
        </nav>
      </header>

      {!showVideo ? (
        <div className={styles.backgroundLayer} style={backgroundStyle} />
      ) : null}
      {showVideo ? (
        <video
          className={styles.video}
          src={variant.backgroundVideoUrl}
          poster={variant.backgroundVideoPosterUrl || undefined}
          autoPlay
          muted
          loop
          playsInline
        />
      ) : null}
      <div className={styles.backgroundMesh} />
      <div className={styles.backgroundGlow} />
      <div
        className={styles.overlay}
        style={{
          background: variant.overlayColor || "rgba(6, 18, 36, 0.56)",
          opacity: overlayOpacity,
        }}
      />

      <main
        className={`${styles.content} ${
          variant.cardPosition === "left"
            ? styles.positionLeft
            : variant.cardPosition === "center"
              ? styles.positionCenter
              : styles.positionRight
        } ${!hasPanelContent ? styles.contentSolo : ""}`}
      >
        <section
          className={`${styles.storyColumn} ${
            variant.contentAlignment === "center"
              ? styles.storyCenter
              : styles.storyLeft
          }`}
        >
          <div className={styles.storyFrame}>
            {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
            {showLogo ? (
              <div className={styles.brand}>
                {logoUrl ? (
                  <img src={logoUrl} alt={siteName || "Clan"} className={styles.logo} />
                ) : (
                  <div className={styles.wordmark}>{siteName}</div>
                )}
              </div>
            ) : null}
            {pageTitle ? <h1 className={styles.title}>{pageTitle}</h1> : null}
            {pageSubtitle ? <p className={styles.subtitle}>{pageSubtitle}</p> : null}
            {variant.showPageContent !== false && page?.content ? (
              <div className={styles.pageContentWrap}>
                <div className={styles.pageContentLabel}>{pageContentLabel}</div>
                <div
                  className={styles.pageContent}
                  dangerouslySetInnerHTML={{ __html: page.content }}
                />
              </div>
            ) : null}
            {variant.showRoleSummary !== false && roleSummary ? (
              <div className={styles.roleSummary}>
                <span className={styles.roleSummaryLabel}>Role</span>
                <strong>{roleSummary.name}</strong>
                {roleSummary.description ? <p>{roleSummary.description}</p> : null}
              </div>
            ) : null}
          </div>
        </section>

        {hasPanelContent ? (
          <div className={styles.panelWrap}>
            <section
              className={`${styles.panel} ${
                variant.surfaceStyle === "glass" ? styles.panelGlass : styles.panelSolid
              }`}
            >
              {panelEyebrow || panelTitle || panelDescription ? (
                <div className={styles.panelHeader}>
                  {panelEyebrow ? (
                    <div className={styles.panelEyebrow}>{panelEyebrow}</div>
                  ) : null}
                  {panelTitle ? <h2 className={styles.panelTitle}>{panelTitle}</h2> : null}
                  {panelDescription ? (
                    <p className={styles.panelDescription}>{panelDescription}</p>
                  ) : null}
                </div>
              ) : null}
              <div className={styles.panelBody}>{children}</div>
              {panelFooter ? <div className={styles.panelFooter}>{panelFooter}</div> : null}
            </section>
          </div>
        ) : null}
      </main>

      {footer ? (
        <div className={styles.footer}>
          <div className={styles.footerInner}>{footer}</div>
        </div>
      ) : null}
    </div>
  );
}
